import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormConfig } from '../../models/form-config';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs/Rx';
import { ApplicationStateService } from '../../services/application-state.service';
import { isYANOSIK, noop } from '../../../core/utils/utils';
import { SectionModeState, SectionsStateService } from '../../services/sections-state.service';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import {UrlParamsService} from '../../../core/services/impl/url-params.service';
import {InteractiveFieldClickEvent} from '../abstract/abstract-field.component';
import {GA4EventKey} from '../../../core/consts/ga4-event-key.enum';
import {GoogleAnalyticsEventsService} from '../../services/google-analytics-events.service';

@Component({
  selector: 'app-section',
  templateUrl: './section.component.html',
  styleUrls: ['./section.component.scss']
})
export class SectionComponent implements OnInit, OnDestroy {
  public static totalCollapsedSections = 0;

  @Input() key: string;
  @Input() sectionRef: TemplateRef<any>;
  @Input() formConfig: FormConfig;
  @Input() formGroup: FormGroup;
  @Input() title: string;
  @Input() upperTitle: string;
  @Input() containerClass: boolean = true;
  @Input() customClass: string;
  @Input() dropdown: boolean = false;
  @Input() expanded: boolean = true;
  @Input() disabled: boolean = true;
  @Input() editMode: boolean = true;
  @Input() verifyDataVisible: boolean = false;

  @ViewChild('dynamicTemplate') dynamicTemplate;
  @ViewChild('expansion') expansion;

  description: string;
  panelToggleState: boolean = false;
  disableOverflow = true;
  statusChanges$: Subscription;
  sectionModeState = SectionModeState;
  previewSectionMode$: Subscription;
  verifyDataTippyProps = {
    placement: 'left',
    distance: 145,
    theme: 'section-verify-data'
  };

  static handleTotalCollapsedSections(value) {
    if (value) {
      SectionComponent.totalCollapsedSections -= 1
    } else {
      SectionComponent.totalCollapsedSections += 1
    }
  }

  constructor(public stateService: ApplicationStateService,
              private googleAnalyticsService: GoogleAnalyticsEventsService,
              private urlParamsService: UrlParamsService,
              private sectionStateService: SectionsStateService) {
  }

  ngOnInit() {
    this.description = this.formConfig ? this.formConfig.description : null;
    this.disableOverflow = this.expanded;

    this.statusChanges$ = this.formGroup.statusChanges.subscribe(value => {
      if (value === 'INVALID') {
        if (!this.expanded) {
          this.expanded = true;
        }
        this.updateSectionModeByFormValidity();
      }
    });

    this.updateSectionModeByFormValidity();
  }

  public ngOnDestroy(): void {
    if (this.statusChanges$) {
      this.statusChanges$.unsubscribe();
    }
    this.previewSectionMode$ ? this.previewSectionMode$.unsubscribe() : noop();
  }

  panelClose() {
    this.disableOverflow = !this.disableOverflow;
    this.panelToggleState = false;
  }

  panelOpened() {
    this.panelToggleState = true;
  }

  panelExpand() {
    this.disableOverflow = !this.disableOverflow;
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    SectionComponent.handleTotalCollapsedSections(this.expanded)
  }

  editModeChange(): void {
    if (this.sectionStateService.sectionState(this.key).mode === this.sectionModeState.PREVIEW) {
      this.sectionStateService.createOrUpdateSectionState(this.key, this.sectionModeState.EDIT);
      this.sectionStateService.sectionState(this.key).pristineClicked = true;
    }
  }

  sendCheckoutElementClickEvent(event: InteractiveFieldClickEvent): void {
    const { fieldLabel, sectionName } = event;
    if (isNotNullOrUndefined(this.googleAnalyticsService)) {
      this.googleAnalyticsService.send(GA4EventKey.PAYMENT_STEP_ELEMENT_CLICK, {
        checkout_section_name: sectionName ? sectionName : this.title,
        checkout_element_name: fieldLabel
      });
    }
  }

  get isPreviewMode(): boolean {
    if (this.urlParamsService.isLoggedUserIdParam || !this.isYanosik) {
      return false;
    }
    if (isNotNullOrUndefined(this.sectionStateService.sectionState(this.key))) {
      return this.sectionStateService.sectionState(this.key).mode === this.sectionModeState.PREVIEW;
    }
    return false;
  }

  get containerInPreviewMode(): boolean {
    if (isNotNullOrUndefined(this.formConfig)) {
      const atLeastOneFieldDisplayedInPreviewMode = isNotNullOrUndefined(this.formConfig.fieldsConfig.find(config => config.displayInPreviewMode));
      return this.isPreviewMode && atLeastOneFieldDisplayedInPreviewMode;
    }
    return false;
  }

  get isYanosik(): boolean {
    return isYANOSIK();
  }

  get invalidControlsInPreviewPart(): boolean {
    if (this.formConfig) {
      const previewModeFields = this.formConfig.fieldsConfig.filter(config => config.displayInPreviewMode).map(field => field.fieldKey);
      const invalidFields = Object.keys(this.formGroup.controls).filter(fieldKey => !this.formGroup.controls[fieldKey].valid);
      return !!previewModeFields.find(fieldKey => invalidFields.includes(fieldKey));
    }
    return false;
  }

  private updateSectionModeByFormValidity(): void {
    if (this.isPreviewMode && this.invalidControlsInPreviewPart) {
      this.editModeChange();
    }
  }
}
